<script setup lang="ts">
    import { storeToRefs } from 'pinia';
    import { useAiAssistantStore } from '~/stores/aiAssistantStore';
    import { useAiAssistant } from '~/composables/useAiAssistant';
    import { SPECIAL_SELECTION } from '~/utils/aiEntityMeta';
    import { INLINE_FORM_KIND } from '~/stores/aiAssistantTypes';
    import type { FreeLineData, CreateContactData, CreateWorksiteData, VerificationState } from '~/stores/aiAssistantTypes';

    const { t } = useTranslation();
    const store = useAiAssistantStore();
    const { messages, isProcessing, currentStreamingMessage, statusLabel, verifications, createdDocumentType } = storeToRefs(store);
    const { sendMessage, abort } = useAiAssistant();

    const extractSearchTerm = (question: string): string => {
        const match = question.match(/["«“]([^"»”]+)["»”]/);
        return match ? match[1] : '';
    };

    const detectFormKindForCreateNew = (question: string) => {
        const q = question.toLowerCase();
        if (q.includes('contact')) return INLINE_FORM_KIND.CREATE_CONTACT;
        if (q.includes('chantier')) return INLINE_FORM_KIND.CREATE_WORKSITE;
        return null;
    };

    const handleOpenInlineForm = (parentMessageId: string, value: string) => {
        const parent = store.messages.find((m) => m.id === parentMessageId);
        if (!parent) return;
        const searchTerm = extractSearchTerm(parent.content);
        if (value === SPECIAL_SELECTION.FreeLine) {
            store.openInlineForm(parentMessageId, INLINE_FORM_KIND.FREE_LINE, searchTerm);
            return;
        }
        if (value === SPECIAL_SELECTION.CreateNew) {
            const kind = detectFormKindForCreateNew(parent.content);
            if (kind) store.openInlineForm(parentMessageId, kind, searchTerm);
        }
    };

    const handleCancelForm = (formMessageId: string) => {
        store.closeInlineForm(formMessageId);
    };

    const handleSubmitForm = async (
        parentMessageId: string,
        formMessageId: string,
        payload: { freeLineData?: FreeLineData; createContactData?: CreateContactData; createWorksiteData?: CreateWorksiteData; selectedExistingUuid?: string },
    ) => {
        // Form bubble is done — remove it. The parent disambig still holds the chosen option.
        store.closeInlineForm(formMessageId);

        if (payload.selectedExistingUuid) {
            await handleOptionSelected(parentMessageId, payload.selectedExistingUuid);
            return;
        }
        if (payload.freeLineData) {
            await handleOptionSelected(parentMessageId, SPECIAL_SELECTION.FreeLine, { free_line_data: payload.freeLineData });
            return;
        }
        if (payload.createContactData) {
            await handleOptionSelected(parentMessageId, SPECIAL_SELECTION.CreateNew, { create_contact_data: payload.createContactData });
            return;
        }
        if (payload.createWorksiteData) {
            await handleOptionSelected(parentMessageId, SPECIAL_SELECTION.CreateNew, { create_worksite_data: payload.createWorksiteData });
        }
    };

    const inputBar = ref<{ submitText: (text: string) => void } | null>(null);

    const handleOptionSelected = async (messageId: string, value: string, extraMetadata?: Record<string, any>) => {
        const freeLineData = extraMetadata?.free_line_data ?? undefined;
        const { allDone, selections } = store.handleBatchedSelection(messageId, value, freeLineData);

        if (!allDone) {
            return;
        }

        const metadata: Record<string, any> = {
            interactive_response: selections.map((s) => ({
                question: s.question,
                selected: s.selected,
                options: s.options,
                ...(s.freeLineData ? { free_line_data: s.freeLineData } : {}),
            })),
        };

        const lastLabel = selections.map((s) => {
            if (s.selected === SPECIAL_SELECTION.FreeLine && s.freeLineData) {
                return t('ai.chat.free_line_summary', { title: s.freeLineData.title });
            }
            const option = s.options.find((o) => o.value === s.selected);
            return option?.label ?? s.selected;
        }).join(', ');

        await sendMessage('selection', metadata, lastLabel);
    };

    const handleSend = async (text: string) => {
        if (isProcessing.value) return;
        await sendMessage(text);
    };

    const handleStop = () => {
        abort();
        store.finalizeStream();
    };

    const handleDismissVerification = (documentId: string) => {
        store.verifications.delete(documentId);
    };

    const handleToggleMinimizeVerification = (documentId: string) => {
        store.toggleVerificationMinimized(documentId);
    };

    const minimizedVerifications = computed(() => {
        const entries: [string, VerificationState][] = [];
        for (const [docId, v] of verifications.value) {
            if (v.minimized) entries.push([docId, v]);
        }
        return entries;
    });

    const handleSuggestionClicked = (text: string) => {
        inputBar.value?.submitText(text);
    };
</script>

<template>
    <div class="flex flex-col flex-1 min-h-0 px-5 pb-5">
        <AiMessagesList
            :messages="messages"
            :current-streaming-message="currentStreamingMessage"
            :status-label="statusLabel"
            :is-processing="isProcessing"
            :verifications="verifications"
            :created-document-type="createdDocumentType"
            @option-selected="handleOptionSelected"
            @suggestion-clicked="handleSuggestionClicked"
            @open-inline-form="handleOpenInlineForm"
            @submit-form="handleSubmitForm"
            @cancel-form="handleCancelForm"
            @dismiss-verification="handleDismissVerification"
            @toggle-minimize-verification="handleToggleMinimizeVerification"
        />

        <div class="flex-shrink-0 max-w-[720px] w-full mx-auto pt-3">
            <div
                v-if="minimizedVerifications.length > 0"
                class="flex flex-wrap gap-2 mb-2"
            >
                <AiVerificationCard
                    v-for="[docId, verification] in minimizedVerifications"
                    :key="`min-${docId}`"
                    :document-id="docId"
                    :document-type="createdDocumentType ?? 'quote'"
                    :steps="verification.steps"
                    :is-complete="verification.isComplete"
                    :final-status="verification.finalStatus"
                    :minimized="true"
                    @dismiss="handleDismissVerification"
                    @toggle-minimize="handleToggleMinimizeVerification"
                />
            </div>
            <AiInputBar
                ref="inputBar"
                :is-processing="isProcessing"
                @send="handleSend"
                @stop="handleStop"
            />
            <p class="mt-[10px] text-center font-inter text-[10px] leading-[1.5] text-gray-400">
                {{ $t('ai.chat.disclaimer') }}
            </p>
        </div>
    </div>
</template>
